import { Drawer, InsightIcon, SkeletonLoader } from '@toggle/design-system';
import { useDisableScroll } from '@toggle/helpers';
import { InsightDirection } from '@toggle/toggle';
import { isSameDay } from 'date-fns';
import React, { ReactNode, useEffect } from 'react';

import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { useGetInsightWidgetData } from '~/services/insights/insights-service';
import { getConditionIcons } from '~/views/insights/components/insight-icons/utils';
import { InsightIconsRoot } from '~/views/insights/components/insight-icons-root/InsightIconsRoot';
import { useExploreStore } from '~/views/insights/hooks/use-explore-store/useExploreStore';

import { InsightsDrawerContent } from './components/insights-drawer-content/InsightsDrawerContent';
import styles from './InsightsDrawer.module.scss';

export type InsightsDrawerProps = {
  insightId: string;
};

export const InsightsDrawer = ({ insightId }: InsightsDrawerProps) => {
  useDisableScroll();
  const { closeDrawer } = useInsightDrawer();
  const { data, isFetching } = useGetInsightWidgetData(insightId);

  const { selectedExploreInsight, setSelectedExploreInsight } = useExploreStore(
    state => ({
      selectedExploreInsight: state.selectedExploreInsight,
      setSelectedExploreInsight: state.setSelectedExploreInsight,
    })
  );

  useEffect(() => {
    return () => {
      setSelectedExploreInsight();
    };
  }, []);

  const getDrawerContent = () => {
    if (isFetching) {
      return (
        <div className={styles.loadingSkeletonWrapper}>
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '100%',
                  height: 'calc(100vh - 80px)',
                  borderRadius: '8px',
                },
              },
            ]}
          />
        </div>
      );
    }

    if (data?.error || !data?.data) {
      return <ErrorMessageWrapper variant="empty" />;
    }

    let icon: ReactNode = null;

    if (selectedExploreInsight) {
      const conditions = getConditionIcons({
        conditions: selectedExploreInsight.conditions,
        kind: selectedExploreInsight.metadata.kind,
      });
      const isCombined = conditions.length > 1;
      const isNewInsight = isSameDay(
        new Date(selectedExploreInsight.created_at),
        new Date()
      );
      icon = (
        <InsightIconsRoot
          conditions={conditions}
          direction={selectedExploreInsight.direction}
          isStarred={isNewInsight}
          isCombined={isCombined}
        />
      );
    } else {
      icon = (
        <InsightIcon
          isBullish={
            data.data.insight.metadata.direction === InsightDirection.Bullish
          }
          showSparkles={isSameDay(
            new Date(data.data.insight.created_at),
            new Date()
          )}
          options={{ iconSize: 32 }}
        />
      );
    }

    return (
      <InsightsDrawerContent
        insightsData={data.data}
        header={() => (
          <div className={styles.header}>
            {icon}
            <div className={styles.title}>
              {data.data.insight?.simple_title ?? data.data.insight.card.name}
            </div>
          </div>
        )}
      />
    );
  };

  return (
    <Drawer data-testid="insights-drawer" isOpen handleCloseClick={closeDrawer}>
      {getDrawerContent()}
    </Drawer>
  );
};
